import React, { useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useServiceLogCorrection } from '../../../hooks/analysis/useServiceLogCorrection';
import { ServiceLogRef, serviceLogValueError } from '../../../types/serviceLogs';

interface CorrectServiceLogValueModalProps {
    logRef: ServiceLogRef;
    serviceTitle: string;
    currentValue: number;
    unit?: string;
    onHide: () => void;
    onSaved: () => void;
}

/** Accepts both "2,5" and "2.5"; an empty field is NaN. */
const parseInput = (text: string): number => {
    const trimmed = text.trim().replace(',', '.');
    return trimmed === '' ? NaN : Number(trimmed);
};

/** Admin modal that corrects the value of one logged service. */
const CorrectServiceLogValueModal: React.FC<CorrectServiceLogValueModalProps> = ({
    logRef, serviceTitle, currentValue, unit, onHide, onSaved,
}) => {
    const [valueText, setValueText] = useState(String(currentValue));
    const [reason, setReason] = useState('');
    const [touched, setTouched] = useState(false);
    const { save, saving, error } = useServiceLogCorrection();

    const parsed = parseInput(valueText);
    const validationError = serviceLogValueError(parsed, currentValue);
    // An unchanged value only disables Spara; it is not shown as an error
    const unchanged = parsed === currentValue;
    const inputError = unchanged ? null : validationError;

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setTouched(true);
        if (validationError) return;
        if (await save(logRef, parsed, reason)) onSaved();
    };

    return (
        <Modal show onHide={saving ? undefined : onHide} centered>
            <Form onSubmit={handleSubmit} noValidate>
                <Modal.Header closeButton={!saving}>
                    <Modal.Title as="h5">Korrigera {serviceTitle.toLowerCase()}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    <p className="text-muted mb-3">
                        Loggat värde: <strong>{currentValue}{unit ? ` ${unit}` : ''}</strong>
                    </p>
                    <Form.Group className="mb-3" controlId="serviceLogValue">
                        <Form.Label>Nytt värde{unit ? ` (${unit})` : ''}</Form.Label>
                        <Form.Control
                            type="text"
                            inputMode="decimal"
                            value={valueText}
                            onChange={e => setValueText(e.target.value)}
                            onBlur={() => setTouched(true)}
                            isInvalid={touched && inputError !== null}
                            autoFocus
                        />
                        <Form.Control.Feedback type="invalid">{inputError}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group controlId="serviceLogReason">
                        <Form.Label>Anledning (valfritt)</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            placeholder="T.ex. felskrivet antal"
                        />
                    </Form.Group>
                    <Form.Text className="text-muted">
                        Det tidigare värdet sparas i historiken.
                    </Form.Text>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={onHide} disabled={saving}>Avbryt</Button>
                    <Button type="submit" variant="primary" disabled={saving || unchanged || (touched && inputError !== null)}>
                        {saving && <Spinner animation="border" size="sm" className="me-2" />}
                        Spara
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default CorrectServiceLogValueModal;
